import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type { VersammlungsstaetteStand, VstKreis, VstMusterzeile, VstRaum, VstVorgabe } from './elektro/versammlungsstaette';
import { BEREICH_HINWEIS, PDF_HINWEIS } from './elektro/versammlungsstaette';
import { zerlegeBefund } from './elektro/blitzschutz';
import { PageHeader } from '../rahmen/PageHeader';
import './elektro/blitzschutz.css';
import {
  ContentSection, DataTable, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Versammlungsstätte nach DIN VDE 0100-718 (EK-Folgepaket, 17.10.2026) — Abschnitt „Trassen und Kabel".
 *
 * Die Seite LIEST `GET …/elektro/versammlungsstaette`: die Bereiche für Besucher und die Rettungswege
 * (über Muster, mit Grenzfällen und Ausschlüssen zur Prüfung), RCD an Steckdosenkreisen, die Aufteilung
 * der allgemeinen Beleuchtung in Räumen für Besucher und die Leitungen in Rettungswegen.
 */
export function ElektroVersammlungsstaette() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <VersammlungsstaetteAnsicht api={api} projektId={projekt.id} />;
}

function zustandVon(status: string): Zustand {
  return status === 'warnung' ? 'warnung' : status === 'ok' || status === 'zugeordnet' ? 'gut' : 'info';
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

export function VersammlungsstaetteAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<VersammlungsstaetteStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroVersammlungsstaette(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'pdf' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'pdf') await api.elektroVersammlungsstaettePdfLaden(projektId);
      else await api.elektroVersammlungsstaetteListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('pdf'); }}>
        Nachweis als PDF
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Versammlungsstätte (DIN VDE 0100-718)"
        beschreibung="Bereiche für Besucher und Rettungswege, RCD an Steckdosenkreisen, Aufteilung der allgemeinen Beleuchtung auf Stromkreise und die Bauart der Leitungen in Rettungswegen. Gerechnet beim Abruf, nur lesend."
        aktionen={aktionen}
      />
      <p className="hinweis">{PDF_HINWEIS}</p>
      {fehler && <ErrorState titel="Die Regelprüfung Versammlungsstätte konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && <Inhalt stand={stand} />}
    </div>
  );
}

function Inhalt({ stand }: { stand: VersammlungsstaetteStand }) {
  const k = stand.kennzahlen;
  return (
    <>
      <p className="hinweis hinweis-warnung" data-testid="bereich-hinweis">{BEREICH_HINWEIS}</p>
      <KpiReihe>
        <KpiCard titel="Räume für Besucher" wert={k.besucherraeume} vergleich={`${k.raeume} Räume im Raumbuch`} />
        <KpiCard titel="Rettungswegräume" wert={k.rettungswegraeume}
                 vergleich={`${k.grenzfaelle} Grenzfälle · ${k.ausgeschlossen} ausgeschlossen`} />
        <KpiCard titel="Steckdosenkreise ohne RCD-Angabe" wert={k.steckdosenOffen}
                 zustand={k.steckdosenOffen > 0 ? 'warnung' : 'gut'} zustandText={`von ${k.steckdosenkreise}`} />
        <KpiCard titel="Besucherräume < 2 Lichtkreise" wert={k.besucherraeumeWarnung}
                 zustand={k.besucherraeumeWarnung > 0 ? 'warnung' : 'gut'} zustandText={`${k.besucherraeumeOffen} ohne Kreis`} />
        <KpiCard titel="Rettungsweg nicht halogenfrei" wert={k.rettungswegWarnung}
                 zustand={k.rettungswegWarnung > 0 ? 'warnung' : 'gut'} zustandText={`von ${k.rettungswegKreise} Kreisen`} />
      </KpiReihe>
      <Bereiche zeilen={stand.bereiche} />
      <Beleuchtung zeilen={stand.beleuchtung} />
      <Kreise titel="R1 Steckdosenkreise bis 32 A: zusätzlicher Schutz durch RCD" zeilen={stand.steckdosen} name="Steckdosenkreise" />
      <Kreise titel="R3 Stromkreise mit Bauteilen in Rettungswegen" zeilen={stand.rettungswege} name="Kreise" />
      <Vorgaben zeilen={stand.vorgaben} />
      {stand.befunde.length > 0 && (
        <ContentSection titel="Befunde">
          <Befundliste befunde={stand.befunde} />
        </ContentSection>
      )}
    </>
  );
}

function Bereiche({ zeilen }: { zeilen: VstMusterzeile[] }) {
  const spalten: Spalte<VstMusterzeile>[] = [
    { schluessel: 'b', titel: 'Bereich', wert: (z) => (z.bereich === 'besucher' ? 'Besucher' : 'Rettungsweg') },
    {
      schluessel: 's', titel: 'Status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={z.status === 'grenzfall' ? 'warnung' : zustandVon(z.status)} text={z.status} klein />,
    },
    { schluessel: 'n', titel: 'Raumname', wert: (z) => z.name ?? '—' },
    { schluessel: 'u', titel: 'Nutzung', wert: (z) => z.nutzung ?? '—' },
    { schluessel: 'm', titel: 'Muster', wert: (z) => z.muster },
    { schluessel: 'a', titel: 'Ausschluss', wert: (z) => z.ausschluss ?? '—' },
    { schluessel: 'r', titel: 'Räume', art: 'zahl', wert: (z) => z.raeume },
  ];
  return (
    <ContentSection titel="Bereiche: zugeordnete Räume je Muster" beschreibung={BEREICH_HINWEIS}>
      <div data-testid="bereiche">
        <DataTable name={`Raumnamen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                   schluessel={(z) => `${z.bereich}-${z.status}-${z.name ?? ''}-${z.nutzung ?? ''}-${z.muster}`} dicht />
      </div>
    </ContentSection>
  );
}

function Beleuchtung({ zeilen }: { zeilen: VstRaum[] }) {
  const [nurAuffaellig, setNurAuffaellig] = useState(true);
  const sicht = nurAuffaellig ? zeilen.filter((z) => z.status !== 'ok') : zeilen;
  const spalten: Spalte<VstRaum>[] = [
    { schluessel: 'r', titel: 'Raum', wert: (z) => z.raumnummer },
    { schluessel: 'n', titel: 'Raumname', wert: (z) => z.name ?? '' },
    { schluessel: 'u', titel: 'Nutzung', wert: (z) => z.nutzung ?? '' },
    { schluessel: 'g', titel: 'Geschoss', wert: (z) => z.geschoss ?? '' },
    { schluessel: 'k', titel: 'Kreise', art: 'zahl', wert: (z) => z.kreise },
    { schluessel: 'kz', titel: 'Stromkreise', wert: (z) => z.kennzeichen.join(', ') },
    {
      schluessel: 's', titel: 'Status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={zustandVon(z.status)} text={z.status} klein />,
    },
  ];
  return (
    <ContentSection titel="R2 Allgemeine Beleuchtung in Räumen für Besucher"
                    beschreibung="Die allgemeine Beleuchtung (ohne Sicherheitsbeleuchtung) soll auf mindestens zwei Stromkreise verteilt sein.">
      <label className="klein">
        <input type="checkbox" checked={nurAuffaellig} onChange={(e) => setNurAuffaellig(e.target.checked)} /> nur Warnungen und offene
      </label>
      <DataTable name={`Räume (${sicht.length} von ${zeilen.length})`} spalten={spalten} zeilen={sicht}
                 schluessel={(z) => z.raumnummer} dicht />
    </ContentSection>
  );
}

function Kreise({ titel, zeilen, name }: { titel: string; zeilen: VstKreis[]; name: string }) {
  const [nurAuffaellig, setNurAuffaellig] = useState(true);
  const sicht = nurAuffaellig ? zeilen.filter((z) => z.status !== 'ok') : zeilen;
  const spalten: Spalte<VstKreis>[] = [
    { schluessel: 'v', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'k', titel: 'Kreis', wert: (z) => z.kreis },
    { schluessel: 'b', titel: 'Bezeichnung', wert: (z) => z.bezeichnung ?? '' },
    { schluessel: 'w', titel: 'Wert', wert: (z) => z.wert },
    {
      schluessel: 's', titel: 'Status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={zustandVon(z.status)} text={z.status} klein />,
    },
  ];
  return (
    <ContentSection titel={titel}>
      <label className="klein">
        <input type="checkbox" checked={nurAuffaellig} onChange={(e) => setNurAuffaellig(e.target.checked)} /> nur Warnungen und offene
      </label>
      <DataTable name={`${name} (${sicht.length} von ${zeilen.length})`} spalten={spalten} zeilen={sicht}
                 schluessel={(z) => `${z.regel}-${z.verteiler}-${z.kreis}`} dicht />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: VstVorgabe[] }) {
  const spalten: Spalte<VstVorgabe>[] = [
    { schluessel: 'f', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'w', titel: 'Wert', wert: (z) => z.wert },
    { schluessel: 'e', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'h', titel: 'Herkunft', wert: (z) => z.herkunft + (z.rueckfall ? ' (Rückfall)' : '') },
    {
      schluessel: 'b', titel: 'bestätigt', wert: (z) => (z.bestaetigt ? 1 : 0),
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein />,
    },
  ];
  return (
    <ContentSection titel="Vorgaben" beschreibung="Alle Vorgaben entstehen unbestätigt (Migration 1140).">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.feld}-${z.verwendung ?? ''}`} dicht />
    </ContentSection>
  );
}
